import React, { useState } from "react";
import { Button } from "primereact/button";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import { InputNumber, InputNumberValueChangeEvent } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import useScadaStore from "store/scada-store";
import { ColumnBodyOptions } from "primereact/column";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { Dialog } from "primereact/dialog";
import DataTable from "components/basic/data-table/DataTable";
import { T } from "services/lang/LanguageService";
import { v4 as uuidv4 } from "uuid";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { TableColumn } from "components/pages/measurement-devices/utils/types";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import ScadaTagEditor from "./ScadaTagEditor";
import { ScadaProps, ScadaFreeTextTagTable, ScadaFreeTextTagTableRow } from "../scadaTypes";
import { defaultFreeText } from "../utils/default-values";

interface ExtendedColumnBodyOptions extends ColumnBodyOptions {
  tableId?: string;
}

const TextTagTable: React.FC<{
  currentConfig: ScadaProps;
  setCurrentConfig: (config: ScadaProps) => void;
  onConfigChange: (config: ScadaProps) => void;
}> = ({ currentConfig, setCurrentConfig, onConfigChange }) => {
  const [dialogVisible, setDialogVisible] = useState(false);
  const [datasetDialogVisible, setDatasetDialogVisible] = useState(false);
  const [targetTableId, setTargetTableId] = useState<string | null>(null);
  const [editingRow, setEditingRow] = useState<{ tableId: string; rowId: string } | null>(null);

  const updateFreeTextTables = useScadaStore(state => state.updateFreeTextTables);

  const handleTablesChange = (config: ScadaProps) => {
    onConfigChange(config);
    setCurrentConfig(config);
    updateFreeTextTables(currentConfig.id!, config.freeTextTagTables);
  };

  const updateConfig = (updatedTables: ScadaFreeTextTagTable[]) => {
    const newConfig = { ...currentConfig, freeTextTagTables: updatedTables };
    handleTablesChange(newConfig);
  };

  const addTable = () => {
    const newTable: ScadaFreeTextTagTable = {
      id: uuidv4(),
      x: 50,
      y: 50,
      rows: [],
    };
    updateConfig([...(currentConfig.freeTextTagTables ?? []), newTable]);
  };

  const removeTable = (tableId: string) => {
    easasConfirmDialog({
      header: T("confirm-dialog.confirmation"),
      message: T("confirm-dialog.are-you-sure-you-want-to-delete-this-table"),
      confirmedCallback: () => {
        const updated = currentConfig.freeTextTagTables.filter(t => t.id !== tableId);
        updateConfig(updated);
        easasAlert({
          summary: T("alert.success"),
          severity: "success",
          detail: T("alert.new-row-was-added"),
        });
      },
    });
  };

  const updateTablePosition = (tableId: string, field: "x" | "y", value: number) => {
    const updated = currentConfig.freeTextTagTables.map(t =>
      t.id === tableId ? { ...t, [field]: value } : t
    );
    updateConfig(updated);
  };

  const openDatasetDialog = (tableId: string) => {
    setTargetTableId(tableId);
    setDatasetDialogVisible(true);
  };

  const handleRowDatasetChange = (identifier: string | null, tableId?: string) => {
    if (!tableId || !identifier) return;

    const updated = currentConfig.freeTextTagTables.map(t => {
      if (t.id === tableId) {
        const newRow: ScadaFreeTextTagTableRow = {
          id: uuidv4(),
          text: { ...defaultFreeText, id: uuidv4(), text: "" },
          tag: {
            xCoord: 0,
            yCoord: 0,
            identifier,
            type: "LABEL",
            layer: "",
            bgColor: "",
            fontSize: 0,
          },
        };
        return { ...t, rows: [...t.rows, newRow] };
      }
      return t;
    });
    updateConfig(updated);
    setDatasetDialogVisible(false);
    setTargetTableId(null);

    easasAlert({
      summary: T("alert.success"),
      severity: "success",
      detail: T("alert.new-row-was-added"),
    });
  };

  const removeRow = (tableId: string, rowId: string) => {
    easasConfirmDialog({
      message: T("confirm-dialog.are-you-sure-you-want-to-remove-this-row"),
      header: T("confirm-dialog.confirmation"),
      icon: "pi pi-exclamation-triangle",
      confirmedCallback: () => {
        const updated = currentConfig.freeTextTagTables.map(t => {
          if (t.id === tableId) {
            return { ...t, rows: t.rows.filter(r => r.id !== rowId) };
          }
          return t;
        });
        updateConfig(updated);
        easasAlert({
          summary: T("alert.success"),
          severity: "success",
          detail: T("alert.row-was-removed"),
        });
      },
    });
  };

  const updateRowText = (tableId: string, rowId: string, value: string) => {
    const updated = currentConfig.freeTextTagTables.map(t => {
      if (t.id === tableId) {
        const rows = t.rows.map(r => (r.id === rowId ? { ...r, text: { ...r.text, text: value } } : r));
        return { ...t, rows };
      }
      return t;
    });
    updateConfig(updated);
  };

  const openTagEditor = (tableId: string, rowId: string) => {
    setEditingRow({ tableId, rowId });
    setDialogVisible(true);
  };

  const handleUpdateTableConfig = (
    e: InputSwitchChangeEvent | InputNumberValueChangeEvent,
    tableId: string
  ) => {
    const updated = currentConfig.freeTextTagTables.map(t => {
      if (t.id === tableId) {
        return { ...t, [e.target.id]: e.value };
      }
      return t;
    });
    updateConfig(updated);
  };

  const rowRemoveButtonTemplate = (row: ScadaFreeTextTagTableRow, options: ExtendedColumnBodyOptions) => (
    <Button
      icon="pi pi-times"
      className="p-button-danger p-button-text"
      onClick={() => removeRow(options.tableId ?? "", row.id)}
    />
  );

  const tagEditButtonTemplate = (row: ScadaFreeTextTagTableRow, options: ExtendedColumnBodyOptions) => (
    <Button
      icon="pi pi-pencil"
      className="p-button-text"
      tooltip={T("action.edit")}
      onClick={() => openTagEditor(options.tableId ?? "", row.id)}
    />
  );

  const datasetDropdownTemplate = (row: ScadaFreeTextTagTableRow, options: ExtendedColumnBodyOptions) => (
    <DatasetDropdown
      onChange={identifier => handleRowDatasetChange(identifier, options.tableId ?? "")}
      value={row.tag.identifier}
      idsToHide={currentConfig.freeTextTagTables.flatMap(t => t.rows.map(r => r.tag.identifier))}
    />
  );

  const rowTextEditorTemplate = (row: ScadaFreeTextTagTableRow, options: ExtendedColumnBodyOptions) => (
    <InputText
      value={row.text?.text ?? ""}
      onChange={e => updateRowText(options.tableId ?? "", row.id, e.target.value)}
      className="w-full"
    />
  );

  const columns: TableColumn<ScadaFreeTextTagTableRow>[] = [
    {
      columnKey: "text",
      field: "text",
      header: T("word.text"),
      body: (row, options) => rowTextEditorTemplate(row, options),
    },
    {
      columnKey: "tag.identifier",
      field: "tag.identifier",
      header: T("word.dataset"),
      body: (row, options) => datasetDropdownTemplate(row, options),
    },
    {
      columnKey: "actions",
      header: "",
      body: (row, options) => tagEditButtonTemplate(row, options),
    },
    {
      columnKey: "remove",
      header: "",
      body: (row, options) => rowRemoveButtonTemplate(row, options),
    },
  ];

  return (
    <div className="flex flex-column gap-4">
      {(currentConfig.freeTextTagTables ?? []).map(table => (
        <div key={table.id} className="border-round-lg border-1 surface-border p-3">
          <div className="flex justify-content-between align-items-center mb-3">
            <div className="flex align-items-center gap-3 w-4">
              <span className="font-medium">{T("word.table")}</span>
              <span>X:</span>
              <InputNumber
                value={table.x}
                onValueChange={e => updateTablePosition(table.id, "x", e.value ?? 0)}
                min={0}
                max={100}
                suffix="%"
              />
              <span>Y:</span>
              <InputNumber
                value={table.y}
                onValueChange={e => updateTablePosition(table.id, "y", e.value ?? 0)}
                min={0}
                max={100}
                suffix="%"
              />
            </div>
            <div className="flex flex-row gap-2 align-items-center justify-content-center">
              <label htmlFor="textColWidth">{T("action.text-col-width")}:</label>
              <InputNumber
                id="textColWidth"
                value={table.textColWidth}
                className="w-4"
                onValueChange={e => handleUpdateTableConfig(e, table.id)}
                suffix="px"
                placeholder="px"
              />
            </div>
            <div className="flex flex-row gap-2">
              <label htmlFor="useSeparators">{T("action.use-separators")}</label>
              <InputSwitch
                id="useSeparators"
                checked={table.useSeparators ?? false}
                onChange={e => handleUpdateTableConfig(e, table.id)}
              />
            </div>

            <Button
              icon="pi pi-trash"
              className="p-button-danger p-button-text"
              onClick={() => removeTable(table.id)}
              tooltip={T("action.remove-table")}
            />
          </div>
          <DataTable
            data={table.rows}
            columns={columns.map(col => ({
              ...col,
              body: col.body
                ? (row: ScadaFreeTextTagTableRow, options: ExtendedColumnBodyOptions) =>
                    (
                      col.body as (
                        row: ScadaFreeTextTagTableRow,
                        options: ExtendedColumnBodyOptions
                      ) => React.ReactNode
                    )(row, { ...options, tableId: table.id })
                : undefined,
            }))}
          />
          <Button
            icon="pi pi-plus"
            label={T("action.add-row")}
            className="mt-3 component-editor-button icon-green w-2"
            onClick={() => openDatasetDialog(table.id)}
          />
        </div>
      ))}

      <Button
        icon="pi pi-plus"
        label={T("action.add-table")}
        className="component-editor-button icon-green w-2"
        onClick={addTable}
      />

      <Dialog
        header={T("word.dataset")}
        visible={datasetDialogVisible}
        onHide={() => setDatasetDialogVisible(false)}
        modal
        style={{ width: "40rem" }}
      >
        {targetTableId && (
          <DatasetDropdown onChange={identifier => handleRowDatasetChange(identifier, targetTableId)} />
        )}
      </Dialog>

      {editingRow && (
        <Dialog
          header={T("word.tag")}
          visible={dialogVisible}
          onHide={() => setDialogVisible(false)}
          modal
          draggable
          resizable
          style={{ width: "60vw" }}
        >
          <ScadaTagEditor
            tag={
              currentConfig.freeTextTagTables
                .find(t => t.id === editingRow.tableId)
                ?.rows.find(r => r.id === editingRow.rowId)?.tag ?? undefined
            }
            onClose={() => setDialogVisible(false)}
            config={currentConfig}
            onConfigChange={handleTablesChange}
            isInTextTagTable
            tableId={editingRow.tableId}
            rowId={editingRow.rowId}
          />
        </Dialog>
      )}
    </div>
  );
};

export default TextTagTable;
